// theme.js
import { createTheme } from '@mui/material/styles';

// Your CSS Variables (constants remain the same)
const COLOR_PRIMARY_ACTION_RED = '#D8292F';
const COLOR_PRIMARY_ACTION_RED_DARK = '#B02228';
const COLOR_TEXT_ON_ACTION_RED = '#FFFFFF';

const COLOR_ACCENT_BLUE = '#1A4A73';
const COLOR_ACCENT_BLUE_DARK = '#102F4A';
const COLOR_TEXT_ON_ACCENT_BLUE = '#FFFFFF';

const COLOR_ACCENT_TEAL = '#2A9D8F';
const COLOR_ACCENT_TEAL_DARK = '#1F7A70';
const COLOR_TEXT_ON_ACCENT_TEAL = '#FFFFFF';

const COLOR_BACKGROUND = '#F8F9FA';
const COLOR_SURFACE = '#FFFFFF';
const COLOR_TEXT_PRIMARY = '#212529';
const COLOR_TEXT_SECONDARY = '#495057';
const COLOR_BORDER = '#DEE2E6';
const COLOR_SUCCESS = '#28A745';
const COLOR_ERROR = '#DC3545';

const FONT_FAMILY_SANS_SERIF = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';
const FONT_WEIGHT_NORMAL = 400;
const FONT_WEIGHT_BOLD = 700;

const BORDER_RADIUS_VALUE = 4; // px
const SPACING_UNIT_VALUE = 8; // px

export const appTheme = createTheme({
    palette: {
        primary: {
            main: COLOR_PRIMARY_ACTION_RED,
            dark: COLOR_PRIMARY_ACTION_RED_DARK,
            contrastText: COLOR_TEXT_ON_ACTION_RED,
        },
        secondary: {
            main: COLOR_ACCENT_TEAL,
            dark: COLOR_ACCENT_TEAL_DARK,
            contrastText: COLOR_TEXT_ON_ACCENT_TEAL,
        },
        info: {
            main: COLOR_ACCENT_BLUE,
            dark: COLOR_ACCENT_BLUE_DARK,
            contrastText: COLOR_TEXT_ON_ACCENT_BLUE,
        },
        success: {
            main: COLOR_SUCCESS,
            contrastText: '#FFFFFF',
        },
        error: {
            main: COLOR_ERROR,
            contrastText: '#FFFFFF',
        },
        background: {
            default: COLOR_BACKGROUND,
            paper: COLOR_SURFACE,
        },
        text: {
            primary: COLOR_TEXT_PRIMARY,
            secondary: COLOR_TEXT_SECONDARY,
        },
        divider: COLOR_BORDER,
    },
    typography: {
        fontFamily: FONT_FAMILY_SANS_SERIF,
        fontWeightRegular: FONT_WEIGHT_NORMAL,
        fontWeightMedium: 500,
        fontWeightBold: FONT_WEIGHT_BOLD,
        h1: { fontSize: '2.5rem', fontWeight: FONT_WEIGHT_BOLD, lineHeight: 1.3, marginTop: `${SPACING_UNIT_VALUE * 3}px`, marginBottom: `${SPACING_UNIT_VALUE * 2}px` },
        h2: { fontSize: '2rem', fontWeight: FONT_WEIGHT_BOLD, lineHeight: 1.3, marginTop: `${SPACING_UNIT_VALUE * 3}px`, marginBottom: `${SPACING_UNIT_VALUE * 2}px` },
        h3: { fontSize: '1.75rem', fontWeight: FONT_WEIGHT_BOLD, lineHeight: 1.3, marginTop: `${SPACING_UNIT_VALUE * 3}px`, marginBottom: `${SPACING_UNIT_VALUE * 2}px` },
        h4: { fontSize: '1.5rem', fontWeight: FONT_WEIGHT_BOLD, lineHeight: 1.3, marginTop: `${SPACING_UNIT_VALUE * 3}px`, marginBottom: `${SPACING_UNIT_VALUE * 2}px` },
        h5: { fontSize: '1.25rem', fontWeight: FONT_WEIGHT_BOLD, lineHeight: 1.3, marginTop: `${SPACING_UNIT_VALUE * 3}px`, marginBottom: `${SPACING_UNIT_VALUE * 2}px` },
        h6: { fontSize: '1rem', fontWeight: FONT_WEIGHT_BOLD, lineHeight: 1.3, marginTop: `0px`, marginBottom: `0px` },
        body1: { fontSize: '1rem', lineHeight: 1.6, marginBottom: `${SPACING_UNIT_VALUE}px` },
        body2: { fontSize: '0.875rem', lineHeight: 1.6, color: COLOR_TEXT_SECONDARY, marginBottom: `${SPACING_UNIT_VALUE}px` },
        subtitle1: { fontSize: '1rem', fontWeight: FONT_WEIGHT_BOLD, lineHeight: 1.5 },
        subtitle2: { fontSize: '0.875rem', fontWeight: FONT_WEIGHT_BOLD, lineHeight: 1.5, color: COLOR_TEXT_PRIMARY },
        button: {
            textTransform: 'none',
            fontWeight: FONT_WEIGHT_BOLD,
            letterSpacing: 'normal',
        },
        caption: { fontSize: '0.75rem', color: COLOR_TEXT_SECONDARY },
    },
    shape: {
        borderRadius: BORDER_RADIUS_VALUE,
    },
    spacing: SPACING_UNIT_VALUE,
    components: {
        MuiCssBaseline: {
            // Convert styleOverrides to a function that returns an object
            // This allows using `theme.breakpoints` and JSS syntax
            styleOverrides: (theme) => ({
                body: {
                    backgroundColor: COLOR_BACKGROUND,
                    color: COLOR_TEXT_PRIMARY,
                    fontFamily: FONT_FAMILY_SANS_SERIF,
                    lineHeight: 1.6,
                    margin: 0, // Good, margin is already 0
                    padding: theme.spacing(2), // Optional: define a default padding for larger screens
                                              // If you had no intentional padding before, you might set this to 0 or remove it.
                                              // This example adds 16px padding for desktop.
                    // On mobile screens (default 'sm' breakpoint is 600px width), set padding to 0.
                    [theme.breakpoints.down('sm')]: {
                        padding: 0,
                        // If you want to be more specific about which padding:
                        // paddingLeft: 0,
                        // paddingRight: 0,
                        // paddingTop: 0, // if vertical padding is also an issue
                        // paddingBottom: 0,
                    },
                    // CSS properties in JSS are typically camelCased
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
                // If your app is rendered into a specific div like <div id="root">
                // and that div has padding, you might also want to target it:
                /*
                '#root': { // Uncomment and use if your main app container (e.g., div#root) has padding
                    [theme.breakpoints.down('sm')]: {
                        padding: 0,
                        // paddingLeft: 0, // Be specific if needed
                        // paddingRight: 0,
                    },
                },
                */
                a: {
                    color: COLOR_ACCENT_BLUE,
                    textDecoration: 'none',
                    transition: 'color 0.2s ease-in-out',
                    '&:hover': {
                        color: COLOR_ACCENT_BLUE_DARK,
                        textDecoration: 'underline',
                    },
                },
                // For input autofill styles, use JSS syntax
                'input:-webkit-autofill, textarea:-webkit-autofill, select:-webkit-autofill': {
                  WebkitBoxShadow: `0 0 0 1000px ${COLOR_SURFACE} inset`,
                  boxShadow: `0 0 0 1000px ${COLOR_SURFACE} inset`,
                  WebkitTextFillColor: COLOR_TEXT_PRIMARY,
                  transition: 'background-color 5000s ease-in-out 0s',
                  fontFamily: FONT_FAMILY_SANS_SERIF,
                },
            }),
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: `${BORDER_RADIUS_VALUE}px`,
                    padding: `${SPACING_UNIT_VALUE}px ${SPACING_UNIT_VALUE * 2}px`,
                },
                containedPrimary: {
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                        backgroundColor: COLOR_PRIMARY_ACTION_RED_DARK,
                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
                    },
                },
                outlinedPrimary: {
                    color: COLOR_TEXT_PRIMARY,
                    borderColor: COLOR_PRIMARY_ACTION_RED,
                    '&:hover': {
                        backgroundColor: COLOR_PRIMARY_ACTION_RED,
                        color: COLOR_TEXT_ON_ACTION_RED,
                        borderColor: COLOR_PRIMARY_ACTION_RED,
                    },
                },
                textInherit: {
                    color: COLOR_TEXT_SECONDARY,
                    '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)'
                    }
                }
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: COLOR_SURFACE,
                },
                outlined: {
                    borderColor: COLOR_BORDER,
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    // borderRadius: BORDER_RADIUS_VALUE * 2,
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    padding: `${SPACING_UNIT_VALUE * 2}px ${SPACING_UNIT_VALUE * 3}px`,
                }
            }
        },
        MuiDialogContent: {
            styleOverrides: {
                // root: {
                // padding: `${SPACING_UNIT_VALUE*2.5}px ${SPACING_UNIT_VALUE*3}px`,
                // },
                dividers: {
                    borderColor: COLOR_BORDER,
                }
            }
        },
        MuiDialogActions: {
            styleOverrides: {
                // root: {
                // padding: `${SPACING_UNIT_VALUE}px ${SPACING_UNIT_VALUE*3}px`,
                // }
            }
        },
        MuiChip: {
            styleOverrides: {
                sizeSmall: {
                    fontSize: '0.8125rem',
                },
                outlinedSuccess: {
                    color: COLOR_SUCCESS,
                    borderColor: COLOR_SUCCESS,
                },
                outlinedError: {
                    color: COLOR_ERROR,
                    borderColor: COLOR_ERROR,
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: COLOR_SURFACE,
                    borderRadius: `${BORDER_RADIUS_VALUE}px`,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#adb5bd',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLOR_PRIMARY_ACTION_RED,
                        borderWidth: '2px',
                    },
                },
                notchedOutline: {
                    borderColor: COLOR_BORDER,
                },
                input: {
                    padding: '10px 14px',
                    color: COLOR_TEXT_PRIMARY,
                    backgroundColor: 'transparent', // Ensure this is transparent if root has a bg
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: COLOR_TEXT_SECONDARY,
                    fontSize: '1rem',
                    '&.Mui-focused': {
                        color: COLOR_PRIMARY_ACTION_RED,
                    },
                },
                outlined: {
                    '&.MuiInputLabel-shrink': {
                        backgroundColor: COLOR_SURFACE,
                        padding: '0 4px',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: COLOR_SURFACE,
                    border: `1px solid ${COLOR_BORDER}`,
                    borderTop: `3px solid ${COLOR_PRIMARY_ACTION_RED}`,
                    borderRadius: `${BORDER_RADIUS_VALUE}px`,
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                    marginBottom: `${SPACING_UNIT_VALUE * 2}px`,
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    borderBottom: `1px solid ${COLOR_BORDER}`,
                    padding: `${SPACING_UNIT_VALUE * 2}px`,
                },
                title: {
                    fontWeight: FONT_WEIGHT_BOLD,
                    fontSize: '1.25rem',
                },
                subheader: {
                    color: COLOR_TEXT_SECONDARY,
                    fontSize: '0.875rem',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: `${SPACING_UNIT_VALUE * 2}px`,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: 'rgba(216, 41, 47, 0.08)'
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(216, 41, 47, 0.16)',
                        '&:hover': {
                            backgroundColor: 'rgba(216, 41, 47, 0.24)'
                        }
                    }
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    '&.Mui-checked': {
                        color: COLOR_PRIMARY_ACTION_RED,
                    }
                }
            }
        }
    },
});